import { createContext, useContext } from "react"
import type {
  TachoCardActivity,
  TachoCardCounters,
  TachoCardLanguage,
  TachoCardStopReason,
} from "@/lib/aiWidgetTachoParser"
import { formatMin, type StopReason } from "./tachoCardFormat"

const plVehicles = (n: number) => {
  if (n === 1) return "1 auto"
  const last = n % 10
  const lastTwo = n % 100
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
    ? `${n} auta`
    : `${n} aut`
}

const pl = {
  title: (n: number) => `Czas jazdy · ${plVehicles(n)}`,
  movingTab: (n: number) => `W trasie: kiedy staną (${n})`,
  standingTab: (n: number) => `Stoją: ile mogą jechać (${n})`,
  noneMoving: "Żadne auto nie jest teraz w trasie.",
  noneStanding: "Żadne auto teraz nie stoi.",
  showAll: (n: number) => `Pokaż wszystkich (${n})`,
  twoDrivers: "2 kierowców",
  stop: (clock: string, crew: boolean) => `stop ${crew ? "ok. " : ""}${clock}`,
  canDrive: "może jechać",
  onlyLeft: (m: number) => `może jechać jeszcze tylko ${formatMin(m)}`,
  dayDone: "dzień jazdy zakończony",
  driverSlot: (slot: number) => `kierowca ${slot}`,
  crewMember: (slot: number, label: string) => `Kierowca ${slot} · ${label}`,
  swap: (clock: string) => `zmiana kierowcy o ${clock}, potem jedzie drugi`,
  reasons: {
    break: "obowiązkowa przerwa 45 min",
    today: "koniec dziennego czasu jazdy",
    week: "koniec jazdy (limit tygodnia)",
    twoWeeks: "koniec jazdy (limit 2 tygodni)",
    none: "",
  } satisfies Record<TachoCardStopReason, string>,
  rowCounters: {
    breakMin: "Do przerwy 45 min",
    todayMin: "Dzień",
    weekMin: "Ten tydzień",
    twoWeeksMin: "2 tygodnie",
  } satisfies Record<keyof TachoCardCounters, string>,
  crewCounters: {
    breakMin: "do przerwy",
    todayMin: "dzień",
    weekMin: "tydzień",
    twoWeeksMin: "2 tygodnie",
  } satisfies Record<keyof TachoCardCounters, string>,
  driverCounters: {
    breakMin: "Do przerwy 45 min",
    todayMin: "Dzienny czas jazdy",
    weekMin: "Tygodniowy czas jazdy",
    twoWeeksMin: "Czas jazdy w 2 tygodniach",
  } satisfies Record<keyof TachoCardCounters, string>,
  activities: {
    driving: "jazda",
    work: "inna praca",
    rest: "odpoczynek",
    availability: "dyspozycja",
    unknown: "brak danych",
  } satisfies Record<TachoCardActivity, string>,
  weeklyRest: "Pauza tygodniowa",
  stopTitle: {
    break: "Przerwa",
    today: "Koniec dziennego czasu jazdy",
    week: "Koniec jazdy · limit tygodnia",
    twoWeeks: "Koniec jazdy · limit 2 tygodni",
  } satisfies Record<StopReason, string>,
  mustStartBy: "musi zacząć najpóźniej",
  mustStopBy: "musi stanąć najpóźniej",
  crewStopBy: "załoga musi stanąć ok.",
  inDriving: (d: string) => `za ${d} jazdy`,
  thenDailyRest: ", potem odpoczynek dobowy",
  canDriveTitle: "Może jechać jeszcze",
  crewBudget: "łącznie dla 2 kierowców",
  exhausted: (counter: string) => `wyczerpany: ${counter.toLowerCase()}`,
  restLastsUntil: "trwa do",
  untilRest: (d: string) => `do niej może jechać ${d}`,
  restEndsBy: "Pauza tygodniowa zakończy się najpóźniej",
  lastDay: "to jego ostatni dzień jazdy",
  showCounters: "Pozostały czas jazdy",
  hideCounters: "Ukryj pozostały czas jazdy",
  left: (d: string) => `zostało ${d}`,
  noData: "brak danych",
  reducedRests: "Skrócone odpoczynki dobowe",
  reducedRestsLeft: (n: number) => `pozostało ${n} z 3`,
  noSourceTime: "Tachograf nie podaje czasu danych",
  source: (when: string) => `Dane z tachografu: ${when}`,
  stale: (age: string) => ` · sprzed ${age}, mogą być nieaktualne`,
  weekdays: {
    Sun: "niedz.",
    Mon: "pon.",
    Tue: "wt.",
    Wed: "śr.",
    Thu: "czw.",
    Fri: "pt.",
    Sat: "sob.",
  } as Record<string, string>,
}

export type TachoCardStrings = typeof pl

const en: TachoCardStrings = {
  title: (n) => `Driving time · ${n} ${n === 1 ? "vehicle" : "vehicles"}`,
  movingTab: (n) => `On the road: when they stop (${n})`,
  standingTab: (n) => `Standing: how long they can drive (${n})`,
  noneMoving: "No vehicle is on the road now.",
  noneStanding: "No vehicle is standing now.",
  showAll: (n) => `Show all (${n})`,
  twoDrivers: "2 drivers",
  stop: (clock, crew) => `stop ${crew ? "approx. " : ""}${clock}`,
  canDrive: "can drive",
  onlyLeft: (m) => `only ${formatMin(m)} of driving left`,
  dayDone: "driving day finished",
  driverSlot: (slot) => `driver ${slot}`,
  crewMember: (slot, label) => `Driver ${slot} · ${label}`,
  swap: (clock) => `driver swap at ${clock}, then the other drives`,
  reasons: {
    break: "mandatory 45 min break",
    today: "end of daily driving time",
    week: "end of driving (weekly limit)",
    twoWeeks: "end of driving (two-week limit)",
    none: "",
  },
  rowCounters: {
    breakMin: "Until the 45 min break",
    todayMin: "Day",
    weekMin: "This week",
    twoWeeksMin: "2 weeks",
  },
  crewCounters: {
    breakMin: "until break",
    todayMin: "day",
    weekMin: "week",
    twoWeeksMin: "2 weeks",
  },
  driverCounters: {
    breakMin: "Until the 45 min break",
    todayMin: "Daily driving time",
    weekMin: "Weekly driving time",
    twoWeeksMin: "Driving time in 2 weeks",
  },
  activities: {
    driving: "driving",
    work: "other work",
    rest: "rest",
    availability: "availability",
    unknown: "no data",
  },
  weeklyRest: "Weekly rest",
  stopTitle: {
    break: "Break",
    today: "End of daily driving time",
    week: "End of driving · weekly limit",
    twoWeeks: "End of driving · two-week limit",
  },
  mustStartBy: "must start by",
  mustStopBy: "must stop by",
  crewStopBy: "the crew must stop at about",
  inDriving: (d) => `in ${d} of driving`,
  thenDailyRest: ", then the daily rest",
  canDriveTitle: "Can still drive",
  crewBudget: "combined for 2 drivers",
  exhausted: (counter) => `used up: ${counter.toLowerCase()}`,
  restLastsUntil: "lasts until",
  untilRest: (d) => `can drive ${d} before it`,
  restEndsBy: "The weekly rest ends at the latest",
  lastDay: "this is their last driving day",
  showCounters: "Driving time left",
  hideCounters: "Hide driving time left",
  left: (d) => `${d} left`,
  noData: "no data",
  reducedRests: "Reduced daily rests",
  reducedRestsLeft: (n) => `${n} of 3 left`,
  noSourceTime: "The tachograph gives no data time",
  source: (when) => `Tachograph data: ${when}`,
  stale: (age) => ` · ${age} old, may be out of date`,
  weekdays: {
    Sun: "Sun",
    Mon: "Mon",
    Tue: "Tue",
    Wed: "Wed",
    Thu: "Thu",
    Fri: "Fri",
    Sat: "Sat",
  },
}

export const TACHO_CARD_STRINGS: Record<TachoCardLanguage, TachoCardStrings> = {
  pl,
  en,
}

export const TachoCardStringsContext = createContext<TachoCardStrings>(pl)

export const useTachoCardStrings = () => useContext(TachoCardStringsContext)
